import { supabase } from './supabase'
import { ActivityTypes, logActivity, logActivityOnLinkedParents } from '../utils/activityLogger'
import type { BillingMilestone } from '../types/agreement'

export interface CreateMilestoneInvoiceResult {
  invoiceId: string
  invoiceNumber: string
}

export async function createMilestoneInvoice(
  milestone: BillingMilestone,
  contract: {
    id: string
    customer_id: string
    property_id?: string | null
    payment_terms?: number | string | null
    title?: string
    operational_division_id?: string | null
  }
): Promise<CreateMilestoneInvoiceResult> {
  if (milestone.status !== 'pending') {
    throw new Error('Only pending milestones can be invoiced.')
  }

  const amount = Number(milestone.amount || 0)
  if (amount <= 0) {
    throw new Error('Milestone amount must be greater than zero.')
  }

  const paymentTermsDays = Number(contract.payment_terms) || 30
  const dueDate = new Date()
  dueDate.setDate(dueDate.getDate() + paymentTermsDays)

  const invoiceNumber = `INV-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`

  const { data: invoice, error: invoiceError } = await supabase
    .from('invoices')
    .insert({
      invoice_number: invoiceNumber,
      customer_id: contract.customer_id,
      property_id: contract.property_id || null,
      contract_id: contract.id,
      operational_division_id: contract.operational_division_id || null,
      billing_milestone_id: milestone.id,
      subtotal: amount,
      total: amount,
      balance_due: amount,
      status: 'draft',
      invoice_date: new Date().toISOString().split('T')[0],
      due_date: dueDate.toISOString().split('T')[0],
      payment_terms: paymentTermsDays,
      notes: `Progress billing: ${milestone.label} (${contract.title || 'Contract'})`,
    })
    .select()
    .single()

  if (invoiceError) throw invoiceError
  if (!invoice) throw new Error('Invoice was not created.')

  const lineDescription = `${milestone.label} — ${milestone.percentage}% progress payment`
  const { error: itemError } = await supabase.from('invoice_items').insert({
    invoice_id: invoice.id,
    description: lineDescription,
    quantity: 1,
    unit_price: amount,
    total: amount,
    display_order: 0,
  })

  if (itemError) {
    throw itemError
  }

  const { error: milestoneError } = await supabase
    .from('agreement_billing_milestones')
    .update({
      status: 'invoiced',
      invoice_id: invoice.id,
      invoiced_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', milestone.id)

  if (milestoneError) throw milestoneError

  await logActivity({
    activity_type: ActivityTypes.INVOICE_CREATED,
    entity_type: 'customer',
    entity_id: contract.customer_id,
    description: `Invoice ${invoiceNumber} created for milestone "${milestone.label}"`,
    metadata: {
      child_entity_type: 'invoice',
      child_entity_id: invoice.id,
      contract_id: contract.id,
      milestone_id: milestone.id,
    },
  })

  await logActivityOnLinkedParents({
    parents: {
      customer_id: contract.customer_id,
      property_id: contract.property_id || undefined,
    },
    activity_type: ActivityTypes.INVOICE_CREATED,
    description: `Invoice ${invoiceNumber} created for milestone "${milestone.label}"`,
    metadata: {
      child_entity_type: 'invoice',
      child_entity_id: invoice.id,
      milestone_id: milestone.id,
      contract_id: contract.id,
    },
  })

  return { invoiceId: invoice.id, invoiceNumber: invoice.invoice_number || invoiceNumber }
}
